import React, { useEffect, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { UpdatePreferencesBody } from "target_wasm";
import {
  fetchNotificationPreferences,
  setNotificationPreferences,
} from "@/components/main/utils/notifications/NotificationsApi";
import { SectionRule } from "components/common/SectionRule";
import { Switch } from "components/common/Switch";
import { createLogger } from "debug/DebugingLogger";
import { useFlash } from "@/components/hooks/useFlash";
import { showNotificationGlobal } from "stores/useNotificationStore";

const log = createLogger("SettingsDialog");

interface Prefs {
  email: boolean;
  app: boolean;
}

type Channel = keyof Prefs;

const ROWS: { channel: Channel; label: string; ariaLabel: string }[] = [
  { channel: "email", label: "By email", ariaLabel: "Notifications by email" },
  {
    channel: "app",
    label: "In the app",
    ariaLabel: "Notifications in the app",
  },
];

export const NotificationSwitches: React.FC = () => {
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [saved, flashSaved] = useFlash<Channel>(1600);

  useEffect(() => {
    fetchNotificationPreferences()
      .then((p) => setPrefs({ email: p.in_email_notification, app: p.enabled }))
      .catch((err) => log.error("Failed to load preferences: %o", err));
  }, []);

  const toggle = async (channel: Channel, on: boolean) => {
    if (!prefs) return;
    const previous = prefs;
    const next = { ...prefs, [channel]: on };
    setPrefs(next);
    try {
      await setNotificationPreferences(
        UpdatePreferencesBody.new_js(next.email, next.app),
      );
      flashSaved(channel);
    } catch (error) {
      log.error("Failed to save preferences: %o", error);
      setPrefs(previous);
      showNotificationGlobal("Failed to save notification settings", "error");
    }
  };

  return (
    <section aria-label="Notifications">
      <SectionRule label="Notifications" />
      <div className="flex flex-col">
        {ROWS.map(({ channel, label, ariaLabel }) => (
          <label key={channel} className="settings-row">
            <span className="flex-1 text-fg-regular">{label}</span>
            {saved === channel && (
              <CheckIcon
                sx={{ fontSize: 12, color: "var(--color-success)" }}
                aria-label="Saved"
              />
            )}
            <Switch
              checked={prefs?.[channel] ?? false}
              disabled={!prefs}
              aria-label={ariaLabel}
              onChange={(on) => void toggle(channel, on)}
            />
          </label>
        ))}
      </div>
    </section>
  );
};
